import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { AlertTriangle, Clock, Download, FileText, Image as ImageIcon, Search, TrendingUp } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { useSearchParams } from 'react-router-dom';
import QuotesByMonthSection from '../components/features/usage/QuotesByMonthSection';
import MembersUsageSection from '../components/features/usage/MembersUsageSection';
import {
  usageService, UsageSummary, UsageDaily, UsageMetric,
} from '../services/UsageService';

const BORDER = '#efebe2';
const DEEP = '#2a1ca8';      // agreement accents, primary buttons, meter fill
const LINE = '#4f46e5';      // chart current-month line
const LINE_FILL = '#6866ff'; // chart area fill (low opacity)
const OVER = '#e5484d';      // overage segment / accrued-overage accents

// Client page is quote-centric: the quotes meter leads, sub-usage explains it.
const CHART_ORDER: UsageMetric[] = ['quotes', 'part_queries', 'images', 'pdfs'];
const SUBUSAGE_ORDER: Array<'part_queries' | 'images' | 'pdfs'> = ['part_queries', 'images', 'pdfs'];

const num = (n: number | null | undefined) => Number(n || 0).toLocaleString();

const monthLabel = (month: string, locale: string, short = false) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(locale, {
    month: short ? 'short' : 'long',
    year: short ? undefined : 'numeric',
    timeZone: 'UTC',
  });
};

const dateLabel = (iso: string, locale: string, withYear = true) =>
  new Date(iso).toLocaleDateString(locale, {
    month: 'short', day: 'numeric', year: withYear ? 'numeric' : undefined, timeZone: 'UTC',
  });

const UsagePlanPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  // ?month=YYYY-MM (e.g. coming back from an advisor's page); undefined = current
  const [searchParams] = useSearchParams();
  const [month, setMonth] = useState<string | undefined>(searchParams.get('month') || undefined);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [daily, setDaily] = useState<UsageDaily | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [upgradeState, setUpgradeState] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [s, d] = await Promise.all([
          usageService.getSummary(month),
          usageService.getDaily(month),
        ]);
        if (cancelled) return;
        setSummary(s); setDaily(d);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || t('usage.loadError', { defaultValue: 'Failed to load usage data.' }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const metricLabel = (m: UsageMetric) =>
    t(`usage.metrics.${m}`, {
      defaultValue:
        m === 'quotes' ? 'Quotes'
        : m === 'part_queries' ? 'Part searches'
        : m === 'images' ? 'Images'
        : m === 'pdfs' ? 'PDFs'
        : m === 'orders' ? 'Orders' : 'LLM queries',
    });

  const agreement = summary?.agreement || null;
  const isCurrent = summary?.period?.is_current ?? true;
  const monthEndShort = summary ? dateLabel(summary.period.end, locale, false) : '';

  const money = (cents: number) => {
    const currency = agreement?.currency || 'USD';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const resetsInDays = useMemo(() => {
    if (!summary?.period?.resets_at) return null;
    const ms = new Date(summary.period.resets_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  }, [summary]);

  const quotesUsed = agreement ? agreement.quotes_used : (summary?.metrics?.quotes?.used ?? 0);
  const included = agreement?.included_quotes ?? null;
  const overageQuotes = agreement?.overage_quotes ?? 0;
  const inOverage = Boolean(agreement && overageQuotes > 0);
  const projectedOver = Boolean(
    agreement && included != null
    && agreement.projected_eom_quotes != null && agreement.projected_eom_quotes > included,
  );

  const handleDownload = async () => {
    if (!summary) return;
    setDownloading(true);
    try { await usageService.downloadReport(summary.period.month); } catch { /* keep page usable */ }
    setDownloading(false);
  };

  const handleUpgrade = async () => {
    setUpgradeState('sending');
    try { await usageService.requestUpgrade(); setUpgradeState('sent'); } catch { setUpgradeState('idle'); }
  };

  // ── Cards ────────────────────────────────────────────────────────────────

  const AgreementCard = () => (
    <div className={`bg-white rounded-xl border p-5 flex flex-col ${agreement ? 'border-gray-900' : 'border-gray-200'}`}>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium mb-1">
        {t('usage.agreement.title', { defaultValue: 'Your agreement' })}
      </p>
      {agreement ? (
        <>
          {agreement.plan_name && (
            <p className="text-sm font-semibold text-[#201e1a] mb-0.5">
              {t('usage.agreement.planLabel', { defaultValue: '{{name}} plan', name: agreement.plan_name })}
            </p>
          )}
          <p className="text-4xl font-semibold tracking-tight" style={{ color: DEEP }}>
            {money(agreement.monthly_price_cents)}
            <span className="text-sm font-normal text-[#999999] ml-1">
              /{t('usage.agreement.month', { defaultValue: 'month' })}
            </span>
          </p>
          <div className="text-xs text-[#555] mt-2.5 leading-relaxed space-y-1.5">
            <p>
              <Trans
                i18nKey="usage.agreement.includes"
                values={{ count: num(agreement.included_quotes) }}
                components={{ b: <strong className="text-[#201e1a] font-semibold" /> }}
                defaults="Includes up to <b>{{count}} Quotes</b> per month."
              />
            </p>
            <p>
              {t('usage.agreement.bundle', {
                defaultValue: 'Each Quote includes up to {{searches}} part searches, {{images}} images and {{pdfs}} PDF; additional usage counts as another Quote.',
                searches: agreement.allowances.part_queries,
                images: agreement.allowances.images,
                pdfs: agreement.allowances.pdfs,
              })}
            </p>
            <p>
              {t('usage.agreement.overageRate', {
                defaultValue: 'Additional quotes: {{price}} each, billed monthly in arrears.',
                price: money(agreement.overage_price_cents),
              })}
            </p>
            <p className="text-[#666]">
              {t('usage.agreement.allFeatures', {
                defaultValue: 'All tiers include the complete CATA feature set.',
              })}
            </p>
            <p className="text-[#999999]">
              {t('usage.agreement.noRollover', { defaultValue: 'Unused quotes do not roll over.' })}
            </p>
          </div>
          {summary && isCurrent && (
            <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[11px] font-medium self-start"
                  style={{ backgroundColor: '#eceafd', color: DEEP }}>
              <Clock className="w-3 h-3" />
              {t('usage.resets', { defaultValue: 'Resets' })} {dateLabel(summary.period.resets_at, locale)}
            </span>
          )}
        </>
      ) : (
        <p className="text-sm text-[#555] mt-1 leading-relaxed">
          {t('usage.agreement.none', { defaultValue: 'No billing agreement configured — usage is tracked but not billed. Contact your account manager.' })}
        </p>
      )}
      <div className="mt-auto pt-4">
        <button
          onClick={handleUpgrade}
          disabled={upgradeState !== 'idle'}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-70"
          style={{ backgroundColor: upgradeState === 'sent' ? '#16a34a' : DEEP }}
        >
          {upgradeState === 'sent'
            ? t('usage.upgradeSent', { defaultValue: 'Request sent' })
            : t('usage.agreement.requestChange', { defaultValue: 'Request a plan change' })}
        </button>
      </div>
    </div>
  );

  const QuoteMeter = () => {
    const pctOfIncluded = included ? Math.round((quotesUsed / included) * 100) : null;
    // Bar geometry: within → navy fill of used/included; over → full navy bar
    // with a red tail segment proportional to the overage share of the total.
    const fillPct = included ? Math.min(100, (Math.min(quotesUsed, included) / Math.max(quotesUsed, included)) * 100) : 0;
    const overPct = included && quotesUsed > included ? 100 - fillPct : 0;
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">
            {t('usage.agreement.quotesThisMonth', { defaultValue: 'Quotes this month' })}
          </p>
          {agreement && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
              inOverage ? 'bg-red-50 text-red-700' : projectedOver ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${inOverage ? 'bg-red-500' : projectedOver ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              {inOverage
                ? t('usage.agreement.statusOverage', { defaultValue: 'In overage' })
                : projectedOver
                  ? t('usage.agreement.statusPace', { defaultValue: 'Trending over' })
                  : t('usage.agreement.statusIncluded', { defaultValue: 'Within included' })}
            </span>
          )}
        </div>
        <p className="text-4xl font-semibold text-[#201e1a] tracking-tight">
          {num(quotesUsed)}
          {included != null && (
            <span className="text-sm font-normal text-[#999999] ml-2">
              {t('usage.of', { defaultValue: 'of' })} {num(included)} {t('usage.agreement.included', { defaultValue: 'included' })}
            </span>
          )}
        </p>
        {included != null ? (
          <>
            <div className="mt-4 h-2 rounded-full overflow-hidden flex" style={{ backgroundColor: '#e6e4fa' }}>
              <div className="h-full" style={{ width: `${fillPct}%`, backgroundColor: DEEP }} />
              {overPct > 0 && <div className="h-full" style={{ width: `${overPct}%`, backgroundColor: OVER }} />}
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className="font-medium" style={{ color: inOverage ? '#dc2626' : '#201e1a' }}>
                {pctOfIncluded}% {t('usage.agreement.ofIncludedUsed', { defaultValue: 'of included used' })}
              </span>
              <span className="text-[#999999]">
                {inOverage
                  ? t('usage.agreement.overBy', { defaultValue: '{{count}} over allowance', count: num(overageQuotes) })
                  : t('usage.remainingCount', { defaultValue: '{{count}} remaining', count: num(Math.max(0, included - quotesUsed)) })}
              </span>
            </div>
            {isCurrent && agreement?.projected_eom_quotes != null && (
              <div className="mt-3 text-xs flex items-center gap-1.5 font-medium"
                   style={{ color: projectedOver ? '#d97706' : '#999999' }}>
                <TrendingUp className="w-3.5 h-3.5" />
                {t('usage.agreement.onPace', {
                  defaultValue: 'On pace for ~{{count}} quotes by {{date}}',
                  count: num(agreement.projected_eom_quotes),
                  date: monthEndShort,
                })}
              </div>
            )}
          </>
        ) : (
          <p className="mt-4 text-xs text-[#999999]">
            {t('usage.agreement.noIncluded', { defaultValue: 'Quote consumption is tracked; no allowance is configured.' })}
          </p>
        )}
      </div>
    );
  };

  const OverageTile = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium mb-3">
        {t('usage.agreement.overageThisMonth', { defaultValue: 'Overage this month' })}
      </p>
      {agreement ? (
        <>
          <p className="text-4xl font-semibold tracking-tight" style={{ color: inOverage ? OVER : '#201e1a' }}>
            {money(agreement.overage_cost_cents)}
          </p>
          <p className="text-xs text-[#999999] mt-2">
            {inOverage
              ? t('usage.agreement.overageBreakdown', {
                  defaultValue: '{{count}} extra quotes × {{price}}',
                  count: num(overageQuotes),
                  price: money(agreement.overage_price_cents),
                })
              : t('usage.agreement.noOverage', { defaultValue: 'You are within your included quotes.' })}
          </p>
          {isCurrent && agreement.projected_overage_cost_cents != null && agreement.projected_overage_cost_cents > 0 && (
            <div className="mt-3 text-xs flex items-center gap-1.5 font-medium text-amber-600">
              <TrendingUp className="w-3.5 h-3.5" />
              {t('usage.agreement.projectedOverage', {
                defaultValue: 'Projected by {{date}}: {{amount}}',
                date: monthEndShort,
                amount: money(agreement.projected_overage_cost_cents),
              })}
            </div>
          )}
          <p className="mt-auto pt-3 text-[11px] text-[#777]">
            {t('usage.agreement.transparentBills', { defaultValue: 'Transparent overage rates — billed monthly in arrears with no surprise bills.' })}
          </p>
        </>
      ) : (
        <p className="text-sm text-[#999999]">
          {t('usage.agreement.noAgreementShort', { defaultValue: 'No agreement configured.' })}
        </p>
      )}
    </div>
  );

  const subUsageIcon = (m: string) =>
    m === 'part_queries' ? <Search className="w-3.5 h-3.5" />
    : m === 'images' ? <ImageIcon className="w-3.5 h-3.5" />
    : <FileText className="w-3.5 h-3.5" />;

  const SubUsageCard = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium mb-3">
        {t('usage.agreement.subUsage', { defaultValue: 'Included in your quotes' })}
      </p>
      <div className="space-y-3">
        {SUBUSAGE_ORDER.map((m) => (
          <div key={m} className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-[#201e1a]">
              <span className="text-[#999999]">{subUsageIcon(m)}</span>
              {metricLabel(m)}
            </span>
            <span className="text-sm font-semibold text-[#201e1a]">
              {num(summary?.metrics?.[m]?.used ?? 0)}
              {agreement && (
                <span className="text-[11px] font-normal text-[#999999] ml-1.5">
                  · {t('usage.agreement.perQuote', {
                    defaultValue: '{{count}} per quote',
                    count: agreement.allowances[m],
                  })}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-auto pt-3 text-[11px] text-[#777]">
        {t('usage.agreement.subUsageHint', { defaultValue: 'Exceeding any allowance rolls the request into another quote.' })}
      </p>
    </div>
  );

  const DailyChart: React.FC<{ metric: UsageMetric }> = ({ metric }) => {
    const series = daily?.series[metric];
    const rows = useMemo(() => {
      if (!series) return [];
      // The x-axis always spans the whole month (the current month's series
      // only runs up to today).
      const [y, m] = (daily?.month || '').split('-').map(Number);
      const daysInMonth = y && m ? new Date(Date.UTC(y, m, 0)).getUTCDate() : series.current.length;
      const n = Math.max(series.current.length, daysInMonth);
      return Array.from({ length: n }, (_, i) => ({
        day: i + 1,
        current: series.current[i]?.count,
      }));
    }, [series, daily?.month]);
    const lastCurrentIdx = (series?.current.length || 0) - 1;
    const curLabel = daily ? monthLabel(daily.month, locale, true) : '';

    // Filled dot on the most recent day of the current month (mock style).
    const endDot = (props: any) => {
      const { cx, cy, index } = props;
      if (index !== lastCurrentIdx || cx == null || cy == null) return <g key={`d-${index}`} />;
      return <circle key={`d-${index}`} cx={cx} cy={cy} r={4.5} fill={LINE} />;
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">
            {metricLabel(metric)} / {t('usage.day', { defaultValue: 'day' })}
          </p>
        </div>
        <ResponsiveContainer width="100%" height={170}>
          <ComposedChart data={rows} margin={{ top: 8, right: 6, bottom: 0, left: -22 }}>
            <CartesianGrid stroke="#f0eeea" vertical={false} />
            <XAxis
              dataKey="day"
              ticks={[1, rows.length]}
              tickFormatter={(d) => `${curLabel} ${d}`}
              tick={{ fontSize: 10, fill: '#999999' }}
              tickLine={false}
              axisLine={{ stroke: BORDER }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#999999' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickCount={3}
              width={42}
            />
            <Tooltip
              formatter={(value: any) => [num(value), metricLabel(metric)]}
              labelFormatter={(day) => `${curLabel} ${day}`}
              contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: BORDER }}
            />
            {/* Fill only — the line below is the one the tooltip reports. */}
            <Area type="linear" dataKey="current" stroke="none" fill={LINE_FILL} fillOpacity={0.12} isAnimationActive={false} tooltipType="none" activeDot={false} />
            <Line type="linear" dataKey="current" stroke={LINE} strokeWidth={2.25} dot={endDot} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <AppLayout pageTitle={t('usage.title', { defaultValue: 'Usage & Plan' })}>
      <MobileHeader title={t('usage.title', { defaultValue: 'Usage & Plan' })} />
      <div className="flex-1 overflow-auto bg-main-bg min-h-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10">
        {/* Header row */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
              {t('usage.yourUsage', { defaultValue: 'Your usage' })}
            </h1>
            {summary && (
              <p className="text-sm text-gray-500 mt-1.5">
                {t('usage.billingMonth', { defaultValue: 'Billing month' })}{' '}
                {dateLabel(summary.period.start, locale, false)} – {dateLabel(summary.period.end, locale)}
                {isCurrent && resetsInDays != null && (
                  <> · {t('usage.resetsIn', { defaultValue: 'usage resets in {{count}} days', count: resetsInDays })}</>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={summary?.period?.month || ''}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white text-gray-900"
            >
              {(summary?.available_months || []).map((m, i) => (
                <option key={m} value={m}>
                  {monthLabel(m, locale)}{i === 0 ? ` — ${t('usage.current', { defaultValue: 'current' })}` : ''}
                </option>
              ))}
            </select>
            <button
              onClick={handleDownload}
              disabled={downloading || !summary}
              className="inline-flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" />
              {t('usage.downloadReport', { defaultValue: 'Download report' })}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>
        )}

        {/* Overage banner — informational (billing continues, nothing is blocked) */}
        {isCurrent && agreement && (inOverage || projectedOver) && (
          <div className={`mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl p-4 ${inOverage ? 'bg-[#fdecec]' : 'bg-amber-50'}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${inOverage ? 'text-red-700' : 'text-amber-600'}`} />
              <div className={`text-sm ${inOverage ? 'text-red-800' : 'text-amber-800'}`}>
                <p className="font-bold">
                  {inOverage
                    ? t('usage.agreement.bannerOverage', {
                        defaultValue: "You've used all {{included}} included quotes — additional quotes are now billed at {{price}} each.",
                        included: num(included),
                        price: money(agreement.overage_price_cents),
                      })
                    : t('usage.agreement.bannerPace', {
                        defaultValue: "You're on pace to exceed your {{included}} included quotes this month.",
                        included: num(included),
                      })}
                </p>
                {inOverage && (
                  <p className="mt-0.5">
                    {t('usage.agreement.bannerAccrued', {
                      defaultValue: '{{amount}} of overage accrued so far, billed monthly in arrears.',
                      amount: money(agreement.overage_cost_cents),
                    })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={upgradeState !== 'idle'}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-70"
              style={{ backgroundColor: upgradeState === 'sent' ? '#16a34a' : inOverage ? OVER : '#d97706' }}
            >
              {upgradeState === 'sent'
                ? t('usage.upgradeSent', { defaultValue: 'Request sent' })
                : t('usage.agreement.requestChange', { defaultValue: 'Request a plan change' })}
            </button>
          </div>
        )}

        {/* Agreement + quote cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <AgreementCard />
          <QuoteMeter />
          <OverageTile />
          <SubUsageCard />
        </div>

        {/* Daily activity */}
        <div className="flex items-baseline gap-2 mb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('usage.dailyActivity', { defaultValue: 'Daily activity' })}
          </h2>
          {daily && (
            <span className="text-xs text-gray-500">
              {monthLabel(daily.month, locale)}, {t('usage.dayByDay', { defaultValue: 'day by day' })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CHART_ORDER.map((m) => <DailyChart key={m} metric={m} />)}
        </div>

        {/* Per-advisor usage (org admins) */}
        <QuotesByMonthSection endMonth={summary?.period?.month} />
        <MembersUsageSection month={summary?.period?.month} />

        {loading && (
          <div className="mt-6 text-sm text-gray-500">{t('usage.loading', { defaultValue: 'Loading…' })}</div>
        )}
      </div>
      </div>
    </AppLayout>
  );
};

export default UsagePlanPage;
